import type { WorkflowVersion } from '@servicetim/shared';
import { useRevertWorkflowVersion, useWorkflowVersions } from '@servicetim/ui/api';
import {
  Badge,
  Button,
  type ColumnDef,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  toast,
  useConfirm,
} from '@servicetim/ui/components';
import { Eye, RotateCcw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { JsonView } from '../../projects/ui/json-view';

export interface VersionsPanelProps {
  projectId: string;
  workflowId: string;
  publishedVersion: number | null;
}

/** Published versions: view the snapshot JSON, revert one into the draft. */
export function VersionsPanel({ projectId, workflowId, publishedVersion }: VersionsPanelProps) {
  const versions = useWorkflowVersions(projectId, workflowId);
  const revert = useRevertWorkflowVersion(projectId);
  const confirm = useConfirm();
  const [viewing, setViewing] = useState<WorkflowVersion | null>(null);

  const onRevert = async (v: WorkflowVersion) => {
    const ok = await confirm({
      title: `Copy version ${v.version} into the draft?`,
      description: 'The current draft is replaced; nothing is published until you publish again.',
      confirmLabel: 'Revert draft',
      onConfirm: () => revert.mutateAsync({ id: workflowId, version: v.version }),
    });
    if (ok) toast.success(`Draft now matches version ${v.version}`);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: handlers and mutation resets are stable for the component lifetime
  const columns = useMemo<ColumnDef<WorkflowVersion>[]>(
    () => [
      {
        accessorKey: 'version',
        header: 'Version',
        size: 120,
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-2 font-medium">
            v{row.original.version}
            {row.original.version === publishedVersion ? (
              <Badge variant="success">Live</Badge>
            ) : null}
          </span>
        ),
      },
      {
        accessorKey: 'publishedAt',
        header: 'Published',
        cell: ({ getValue }) => new Date(getValue<string>()).toLocaleString(),
      },
      {
        id: 'content',
        header: 'Content',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-fg-muted text-sm">
            {row.original.snapshot.statuses.length} statuses,{' '}
            {row.original.snapshot.transitions.length} transitions
            {row.original.statusMapping
              ? `, ${Object.keys(row.original.statusMapping).length} mapped`
              : ''}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        size: 220,
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button variant="outline" size="sm" onClick={() => setViewing(row.original)}>
              <Eye />
              View
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void onRevert(row.original)}
              disabled={revert.isPending}
            >
              <RotateCcw />
              Revert to draft
            </Button>
          </div>
        ),
      },
    ],
    [publishedVersion, revert.isPending],
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={versions.data}
        getRowId={(v) => v.id}
        isLoading={versions.isPending}
        error={versions.error}
        onRetry={() => void versions.refetch()}
        dense
        caption="Published versions"
        emptyTitle="Not published yet"
        emptyDescription="Publish the draft to create the first immutable version."
      />
      <Dialog
        open={viewing !== null}
        onOpenChange={(open) => (open ? undefined : setViewing(null))}
      >
        <DialogContent size="xl">
          <DialogHeader>
            <DialogTitle>Version {viewing?.version} snapshot</DialogTitle>
            <DialogDescription>
              Published {viewing ? new Date(viewing.publishedAt).toLocaleString() : ''}. Tickets run
              against this snapshot while it is live.
            </DialogDescription>
          </DialogHeader>
          {viewing ? <JsonView value={viewing.snapshot} className="max-h-[70vh]" /> : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
